// Cash Register Dialog - Corte de Caja
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Wallet, DollarSign, CreditCard, ArrowRightLeft, 
  CheckCircle2, XCircle, AlertTriangle, Clock, Shield, Edit3, Eye, EyeOff
} from 'lucide-react';
import { API } from './constants';

export function CashRegisterDialog({ 
  open, 
  onOpenChange, 
  branchId, 
  getAuthHeaders,
  onStatusChange,
  userRole // Add user role prop
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openingAmount, setOpeningAmount] = useState('');
  const [closingAmount, setClosingAmount] = useState('');
  const [variableExpense, setVariableExpense] = useState('');
  const [variableExpenseNote, setVariableExpenseNote] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Audit state
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditAmount, setAuditAmount] = useState('');
  const [auditPassword, setAuditPassword] = useState('');
  const [showAuditPassword, setShowAuditPassword] = useState(false);
  const [auditReason, setAuditReason] = useState('');
  const [auditProcessing, setAuditProcessing] = useState(false);

  // Reopen state
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [reopenPassword, setReopenPassword] = useState('');
  const [showReopenPassword, setShowReopenPassword] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenProcessing, setReopenProcessing] = useState(false);

  useEffect(() => {
    if (open && branchId) {
      fetchStatus();
    }
  }, [open, branchId]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/cash-register/status/${branchId}`,
        getAuthHeaders()
      );
      setStatus(response.data);
    } catch (error) {
      toast.error('Error al obtener estado de caja');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRegister = async () => {
    if (!openingAmount || parseFloat(openingAmount) < 0) {
      toast.error('Ingresa un monto válido');
      return;
    }

    setProcessing(true);
    try {
      await axios.post(
        `${API}/cash-register/open`,
        {
          branch_id: branchId,
          opening_amount: parseFloat(openingAmount),
        },
        getAuthHeaders()
      );
      toast.success('Caja abierta correctamente');
      setOpeningAmount('');
      fetchStatus();
      onStatusChange?.(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al abrir caja');
    } finally {
      setProcessing(false);
    }
  };

  const handleCloseRegister = async () => {
    if (!closingAmount || parseFloat(closingAmount) < 0) {
      toast.error('Ingresa el monto de cierre');
      return;
    }

    const expenseAmount = parseFloat(variableExpense) || 0;
    if (expenseAmount < 0) {
      toast.error('El monto de retiro no puede ser negativo');
      return;
    }

    setProcessing(true);
    try {
      await axios.post(
        `${API}/cash-register/close`,
        {
          branch_id: branchId,
          closing_amount: parseFloat(closingAmount),
          variable_expense: expenseAmount,
          variable_expense_note: variableExpenseNote.trim() || null,
        },
        getAuthHeaders()
      );
      toast.success('Caja cerrada correctamente');
      setClosingAmount('');
      setVariableExpense('');
      setVariableExpenseNote('');
      fetchStatus();
      onStatusChange?.(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar caja');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const handleAuditSubmit = async () => {
    if (!auditAmount || parseFloat(auditAmount) < 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    if (!auditPassword) {
      toast.error('Ingresa tu contraseña para confirmar');
      return;
    }

    setAuditProcessing(true);
    try {
      await axios.post(
        `${API}/cash-register/audit`,
        {
          register_id: register?.id,
          new_closing_amount: parseFloat(auditAmount),
          master_password: auditPassword,
          audit_reason: auditReason.trim() || null,
        },
        getAuthHeaders()
      );
      toast.success('Auditoría de caja realizada correctamente');
      setShowAuditDialog(false);
      setAuditAmount('');
      setAuditPassword('');
      setAuditReason('');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al realizar auditoría');
    } finally {
      setAuditProcessing(false);
    }
  };

  const openAuditDialog = () => {
    setAuditAmount(register?.closing_amount?.toString() || '');
    setShowAuditDialog(true);
  };

  const handleReopenSubmit = async () => {
    if (!reopenPassword) {
      toast.error('Ingresa tu contraseña para confirmar');
      return;
    }

    setReopenProcessing(true);
    try {
      await axios.post(
        `${API}/cash-register/reopen`,
        {
          register_id: register?.id,
          master_password: reopenPassword,
          reopen_reason: reopenReason.trim() || null,
        },
        getAuthHeaders()
      );
      toast.success('Caja reabierta correctamente');
      setShowReopenDialog(false);
      setReopenPassword('');
      setReopenReason('');
      fetchStatus();
      onStatusChange?.(true); // Notify parent that register is now open
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reabrir la caja');
    } finally {
      setReopenProcessing(false);
    }
  };

  const register = status?.cash_register;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-gold" />
            Corte de Caja
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-zinc-500">Cargando...</div>
        ) : status?.status === 'not_opened' ? (
          // Cash register not opened - show open form
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-amber-500 mb-2">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">Caja sin abrir</span>
              </div>
              <p className="text-sm text-zinc-400">
                No se puede registrar ventas hasta que se abra la caja del día.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Monto inicial de efectivo</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={openingAmount}
                  onChange={(e) => setOpeningAmount(e.target.value)}
                  placeholder="0.00"
                  className="bg-zinc-800 border-zinc-700 text-white text-lg pl-9"
                  data-testid="opening-amount-input"
                />
              </div>
              <p className="text-xs text-zinc-500">
                Ingresa el efectivo con el que inicia la caja
              </p>
            </div>

            <Button
              onClick={handleOpenRegister}
              disabled={processing}
              className="w-full bg-emerald-500 text-white hover:bg-emerald-600"
              data-testid="open-register-btn"
            >
              {processing ? 'Abriendo...' : 'Abrir Caja'}
            </Button>
          </div>
        ) : status?.status === 'open' ? (
          // Cash register open - show summary and close form
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-500 mb-2">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Caja abierta</span>
              </div>
              <p className="text-xs text-zinc-400">
                Abierta por {register?.opened_by_name} a las{' '}
                {new Date(register?.opened_at).toLocaleTimeString('es-MX', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </p>
            </div>

            {/* Sales Summary */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <Wallet className="w-3 h-3" />
                  Inicio de caja
                </div>
                <p className="text-lg font-semibold text-white">
                  {formatCurrency(register?.opening_amount)}
                </p>
              </div>
              <div className="p-3 bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <Clock className="w-3 h-3" />
                  Transacciones
                </div>
                <p className="text-lg font-semibold text-white">
                  {register?.transactions_count || 0}
                </p>
              </div>
            </div>

            <div className="space-y-2 p-4 bg-zinc-800/50 rounded-lg">
              <h4 className="text-sm font-medium text-zinc-300 mb-3">Ventas del día</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-500" />
                  <span className="text-zinc-400">Efectivo:</span>
                </div>
                <span className="text-white text-right">{formatCurrency(register?.total_cash_sales)}</span>
                
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-500" />
                  <span className="text-zinc-400">Tarjeta:</span>
                </div>
                <span className="text-white text-right">{formatCurrency(register?.total_card_sales)}</span>
                
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4 text-purple-500" />
                  <span className="text-zinc-400">Transferencia:</span>
                </div>
                <span className="text-white text-right">{formatCurrency(register?.total_transfer_sales)}</span>

                {(register?.total_combined_cash > 0 || register?.total_combined_card > 0) && (
                  <>
                    <div className="col-span-2 border-t border-zinc-700 my-2"></div>
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-400 text-xs">Combinado (efectivo):</span>
                    </div>
                    <span className="text-white text-right text-xs">{formatCurrency(register?.total_combined_cash)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-400 text-xs">Combinado (tarjeta):</span>
                    </div>
                    <span className="text-white text-right text-xs">{formatCurrency(register?.total_combined_card)}</span>
                  </>
                )}
              </div>

              <div className="border-t border-zinc-700 mt-3 pt-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gold">Efectivo esperado en caja:</span>
                  <span className="text-lg font-bold text-gold">
                    {formatCurrency(
                      (register?.opening_amount || 0) + 
                      (register?.total_cash_sales || 0) + 
                      (register?.total_combined_cash || 0)
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Close Form */}
            <div className="space-y-3 pt-2 border-t border-zinc-800">
              <h4 className="text-sm font-medium text-zinc-300">Cerrar caja</h4>
              
              {/* Variable Expense Withdrawal */}
              <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-amber-500 text-sm">
                  <Wallet className="w-4 h-4" />
                  <span className="font-medium">Retiro por Gasto Variable</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-xs">Monto a retirar</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={variableExpense}
                        onChange={(e) => setVariableExpense(e.target.value)}
                        placeholder="0.00"
                        className="bg-zinc-800 border-zinc-700 text-white pl-9 h-9"
                        data-testid="variable-expense-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-xs">Concepto (opcional)</Label>
                    <Input
                      type="text"
                      value={variableExpenseNote}
                      onChange={(e) => setVariableExpenseNote(e.target.value)}
                      placeholder="Ej: Compra de insumos"
                      className="bg-zinc-800 border-zinc-700 text-white h-9"
                      data-testid="variable-expense-note-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-zinc-500">
                  Este monto se descontará del efectivo esperado y se registrará como egreso.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Efectivo contado</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={closingAmount}
                    onChange={(e) => setClosingAmount(e.target.value)}
                    placeholder="0.00"
                    className="bg-zinc-800 border-zinc-700 text-white pl-9"
                    data-testid="closing-amount-input"
                  />
                </div>
                {variableExpense && parseFloat(variableExpense) > 0 && (
                  <p className="text-xs text-amber-400">
                    Efectivo esperado después del retiro: {formatCurrency(
                      (register?.opening_amount || 0) + 
                      (register?.total_cash_sales || 0) + 
                      (register?.total_combined_cash || 0) -
                      parseFloat(variableExpense)
                    )}
                  </p>
                )}
              </div>

              <Button
                onClick={handleCloseRegister}
                disabled={processing}
                className="w-full bg-red-500 text-white hover:bg-red-600"
                data-testid="close-register-btn"
              >
                {processing ? 'Cerrando...' : 'Cerrar Caja'}
              </Button>
            </div>
          </div>
        ) : (
          // Cash register closed
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-zinc-800 border border-zinc-700 rounded-lg">
              <div className="flex items-center gap-2 text-zinc-400 mb-2">
                <XCircle className="w-5 h-5" />
                <span className="font-medium">Caja cerrada</span>
              </div>
              <p className="text-sm text-zinc-500">
                Cerrada por {register?.closed_by_name} a las{' '}
                {register?.closed_at && new Date(register.closed_at).toLocaleTimeString('es-MX', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              {register?.last_audit_at && (
                <p className="text-xs text-amber-400 mt-2">
                  ⚠️ Auditada por {register?.last_audit_by_name} - {register?.audit_reason || 'Sin motivo especificado'}
                </p>
              )}
            </div>

            {/* Final Summary */}
            <div className="space-y-3 p-4 bg-zinc-800/50 rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-zinc-400">Inicio:</span>
                <span className="text-white text-right">{formatCurrency(register?.opening_amount)}</span>
                
                <span className="text-zinc-400">Ventas efectivo:</span>
                <span className="text-white text-right">{formatCurrency(register?.total_cash_sales + register?.total_combined_cash)}</span>
                
                {register?.variable_expense > 0 && (
                  <>
                    <span className="text-amber-400">Retiro gasto variable:</span>
                    <span className="text-amber-400 text-right">-{formatCurrency(register?.variable_expense)}</span>
                    {register?.variable_expense_note && (
                      <>
                        <span className="text-zinc-500 text-xs col-span-2 -mt-1">
                          ({register.variable_expense_note})
                        </span>
                      </>
                    )}
                  </>
                )}
                
                <span className="text-zinc-400">Esperado:</span>
                <span className="text-white text-right">{formatCurrency(register?.expected_cash)}</span>
                
                <span className="text-zinc-400">Contado:</span>
                <span className="text-white text-right">{formatCurrency(register?.closing_amount)}</span>
              </div>

              <div className="border-t border-zinc-700 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400">Diferencia:</span>
                  <span className={`text-lg font-bold ${
                    register?.cash_difference === 0 ? 'text-emerald-500' :
                    register?.cash_difference > 0 ? 'text-blue-500' : 'text-red-500'
                  }`}>
                    {register?.cash_difference > 0 ? '+' : ''}{formatCurrency(register?.cash_difference)}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-center text-sm text-zinc-500">
              La caja de hoy ya fue cerrada. Para realizar ventas, abre una nueva caja mañana.
            </p>

            {/* Audit and Reopen Buttons - Only for MasterAdmin */}
            {userRole === 'master_admin' && (
              <div className="pt-2 border-t border-zinc-800 space-y-2">
                <Button
                  onClick={() => setShowReopenDialog(true)}
                  className="w-full bg-green-600 text-white hover:bg-green-700"
                  data-testid="reopen-register-btn"
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  Reabrir Caja
                </Button>
                <Button
                  onClick={openAuditDialog}
                  variant="outline"
                  className="w-full border-amber-500/50 text-amber-500 hover:bg-amber-500/10 hover:text-amber-400"
                  data-testid="audit-register-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Auditoría de Caja
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Audit Dialog */}
        <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                Auditoría de Caja
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-sm text-amber-400">
                  Esta acción corregirá el efectivo contado del cierre de caja. 
                  Se registrará en el historial de auditorías.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-800/50 rounded-lg text-sm">
                <span className="text-zinc-400">Monto actual:</span>
                <span className="text-white text-right">{formatCurrency(register?.closing_amount)}</span>
                <span className="text-zinc-400">Esperado:</span>
                <span className="text-white text-right">{formatCurrency(register?.expected_cash)}</span>
                <span className="text-zinc-400">Diferencia actual:</span>
                <span className={`text-right ${
                  register?.cash_difference === 0 ? 'text-emerald-500' :
                  register?.cash_difference > 0 ? 'text-blue-500' : 'text-red-500'
                }`}>
                  {register?.cash_difference > 0 ? '+' : ''}{formatCurrency(register?.cash_difference)}
                </span>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Nuevo efectivo contado</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={auditAmount}
                    onChange={(e) => setAuditAmount(e.target.value)}
                    placeholder="0.00"
                    className="bg-zinc-800 border-zinc-700 text-white pl-9"
                    data-testid="audit-amount-input"
                  />
                </div>
                {auditAmount && (
                  <p className="text-xs text-zinc-400">
                    Nueva diferencia: {' '}
                    <span className={
                      parseFloat(auditAmount) - (register?.expected_cash || 0) === 0 ? 'text-emerald-500' :
                      parseFloat(auditAmount) - (register?.expected_cash || 0) > 0 ? 'text-blue-500' : 'text-red-500'
                    }>
                      {parseFloat(auditAmount) - (register?.expected_cash || 0) > 0 ? '+' : ''}
                      {formatCurrency(parseFloat(auditAmount) - (register?.expected_cash || 0))}
                    </span>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Motivo de la corrección (opcional)</Label>
                <Input
                  type="text"
                  value={auditReason}
                  onChange={(e) => setAuditReason(e.target.value)}
                  placeholder="Ej: Error de conteo, billete falso detectado..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="audit-reason-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Shield className="w-3 h-3" />
                  Contraseña de MasterAdmin
                </Label>
                <div className="relative">
                  <Input
                    type={showAuditPassword ? 'text' : 'password'}
                    value={auditPassword}
                    onChange={(e) => setAuditPassword(e.target.value)}
                    placeholder="Ingresa tu contraseña para confirmar"
                    className="bg-zinc-800 border-zinc-700 text-white pr-10"
                    data-testid="audit-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAuditPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                    aria-label={showAuditPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    data-testid="audit-password-toggle"
                  >
                    {showAuditPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAuditDialog(false);
                    setAuditPassword('');
                    setAuditReason('');
                  }}
                  className="flex-1 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAuditSubmit}
                  disabled={auditProcessing || !auditPassword}
                  className="flex-1 bg-amber-500 text-black hover:bg-amber-600"
                  data-testid="audit-confirm-btn"
                >
                  {auditProcessing ? 'Procesando...' : 'Confirmar Auditoría'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reopen Dialog */}
        <Dialog open={showReopenDialog} onOpenChange={setShowReopenDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-green-500" />
                Reabrir Caja
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-sm text-green-400">
                  Esta acción reabrirá la caja cerrada permitiendo continuar con las ventas del día.
                  Se registrará en el historial de operaciones.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-800/50 rounded-lg text-sm">
                <span className="text-zinc-400">Cerrada a las:</span>
                <span className="text-white text-right">
                  {register?.closed_at && new Date(register.closed_at).toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
                <span className="text-zinc-400">Cerrada por:</span>
                <span className="text-white text-right">{register?.closed_by_name}</span>
                <span className="text-zinc-400">Efectivo contado:</span>
                <span className="text-white text-right">{formatCurrency(register?.closing_amount)}</span>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Motivo de reapertura (opcional)</Label>
                <Input
                  type="text"
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  placeholder="Ej: Venta pendiente, error al cerrar..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="reopen-reason-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Shield className="w-3 h-3" />
                  Contraseña de MasterAdmin
                </Label>
                <div className="relative">
                  <Input
                    type={showReopenPassword ? 'text' : 'password'}
                    value={reopenPassword}
                    onChange={(e) => setReopenPassword(e.target.value)}
                    placeholder="Ingresa tu contraseña para confirmar"
                    className="bg-zinc-800 border-zinc-700 text-white pr-10"
                    data-testid="reopen-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowReopenPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                    aria-label={showReopenPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    data-testid="reopen-password-toggle"
                  >
                    {showReopenPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowReopenDialog(false);
                    setReopenPassword('');
                    setReopenReason('');
                  }}
                  className="flex-1 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleReopenSubmit}
                  disabled={reopenProcessing || !reopenPassword}
                  className="flex-1 bg-green-600 text-white hover:bg-green-700"
                  data-testid="reopen-confirm-btn"
                >
                  {reopenProcessing ? 'Procesando...' : 'Reabrir Caja'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
